import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, X, Check, Search } from 'lucide-react';

export interface Option {
  id: number;
  label: string;
}

function useOutsideClose(ref: React.RefObject<HTMLElement | null>, onClose: () => void) {
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onClose]);
}

const boxClass =
  'min-h-[2.75rem] w-full rounded-[var(--radius-md)] border border-[var(--color-gray-300)] bg-white px-2 py-1.5 flex items-center gap-1 cursor-pointer focus-within:border-[var(--color-primary-light)] focus-within:ring-2 focus-within:ring-[var(--color-primary-50)]';

const Dropdown: React.FC<{
  options: Option[];
  isSelected: (id: number) => boolean;
  onPick: (id: number) => void;
  emptyText?: string;
}> = ({ options, isSelected, onPick, emptyText = 'No options' }) => {
  const [q, setQ] = useState('');
  const filtered = options.filter((o) => o.label.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="absolute z-30 mt-1 w-full bg-white border border-[var(--color-gray-200)] rounded-[var(--radius-md)] shadow-lg">
      <div className="p-2 border-b border-[var(--color-gray-100)] relative">
        <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--color-gray-400)]" />
        <input
          autoFocus
          className="w-full !pl-8 !min-h-0 !py-2 text-sm border border-[var(--color-gray-200)] rounded-[var(--radius-md)]"
          placeholder="Search…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      <div className="max-h-56 overflow-y-auto py-1">
        {filtered.length === 0 && <div className="px-3 py-2 text-sm text-[var(--color-gray-400)]">{emptyText}</div>}
        {filtered.map((o) => {
          const sel = isSelected(o.id);
          return (
            <button
              key={o.id}
              type="button"
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-[var(--color-primary-50)] ${sel ? 'text-[var(--color-primary)] font-medium' : 'text-[var(--color-gray-700)]'}`}
              onClick={(e) => { e.stopPropagation(); onPick(o.id); }}
            >
              <span>{o.label}</span>
              {sel && <Check size={15} />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ── Multi-select (chips) ─────────────────────────────────────────────────────
export const MultiSelect: React.FC<{
  options: Option[];
  value: number[];
  onChange: (ids: number[]) => void;
  placeholder?: string;
  disabled?: boolean;
}> = ({ options, value, onChange, placeholder = 'Select…', disabled }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  useOutsideClose(ref, () => setOpen(false));

  const selected = options.filter((o) => value.includes(o.id));
  const toggle = (id: number) => (value.includes(id) ? onChange(value.filter((x) => x !== id)) : onChange([...value, id]));

  return (
    <div ref={ref} className="relative">
      <div className={`${boxClass} flex-wrap ${disabled ? 'opacity-60 pointer-events-none' : ''}`} onClick={() => setOpen((o) => !o)}>
        <div className="flex flex-wrap gap-1 flex-1">
          {selected.length === 0 && <span className="text-[var(--color-gray-400)] text-sm px-1">{placeholder}</span>}
          {selected.map((o) => (
            <span key={o.id} className="inline-flex items-center gap-1 bg-[var(--color-primary-50)] text-[var(--color-primary)] text-xs rounded-[var(--radius-sm)] px-2 py-0.5">
              {o.label}
              <button type="button" className="hover:text-[var(--color-primary-dark)]" onClick={(e) => { e.stopPropagation(); toggle(o.id); }}><X size={12} /></button>
            </span>
          ))}
        </div>
        <ChevronDown size={16} className="text-[var(--color-gray-400)] shrink-0" />
      </div>
      {open && !disabled && <Dropdown options={options} isSelected={(id) => value.includes(id)} onPick={toggle} />}
    </div>
  );
};

// ── Single searchable select ─────────────────────────────────────────────────
export const SearchSelect: React.FC<{
  options: Option[];
  value: number | null;
  onChange: (id: number | null) => void;
  placeholder?: string;
  disabled?: boolean;
}> = ({ options, value, onChange, placeholder = 'Select…', disabled }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  useOutsideClose(ref, () => setOpen(false));

  const selected = options.find((o) => o.id === value) || null;

  return (
    <div ref={ref} className="relative">
      <div className={`${boxClass} ${disabled ? 'opacity-60 pointer-events-none' : ''}`} onClick={() => setOpen((o) => !o)}>
        <span className={`flex-1 text-sm px-1 ${selected ? 'text-[var(--color-gray-800)]' : 'text-[var(--color-gray-400)]'}`}>{selected ? selected.label : placeholder}</span>
        {selected && (
          <button type="button" className="text-[var(--color-gray-400)] hover:text-[var(--color-gray-700)]" onClick={(e) => { e.stopPropagation(); onChange(null); }}><X size={14} /></button>
        )}
        <ChevronDown size={16} className="text-[var(--color-gray-400)] shrink-0" />
      </div>
      {open && !disabled && (
        <Dropdown
          options={options}
          isSelected={(id) => id === value}
          onPick={(id) => { onChange(id); setOpen(false); }}
        />
      )}
    </div>
  );
};
